import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import PasswordInput from '../components/PasswordInput';
import CredentialSuccessModal from '../components/CredentialSuccessModal';
import api from '../api';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const mediaUrl = value => value?.startsWith('/') ? value : `/uploads/${value}`;
const digitsOnly = (value, maximum) => String(value || '').replace(/\D/g, '').slice(0, maximum);
const cnicInput = value => {
  const digits = digitsOnly(value, 13);
  if (digits.length <= 5) return digits;
  if (digits.length <= 12) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  return `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12)}`;
};

const emptyForm = {
  name: '', fatherName: '', cnicNo: '', address: '', institute: '',
  department: '', contactNo: '', emergencyParentContact: '', room: '',
  admissionFee: '', securityFee: '', monthlyFee: '',
  admissionFeePaid: 'Unpaid', securityFeePaid: 'Unpaid',
  admissionDate: new Date().toISOString().split('T')[0],
  status: 'Active'
};

export default function StudentForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState(emptyForm);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [signature, setSignature] = useState(null);
  const [roomOptions, setRoomOptions] = useState([]);
  const [accountInfo, setAccountInfo] = useState(null);
  const [accountForm, setAccountForm] = useState({ create: true, username: '', password: '' });
  const [credentials, setCredentials] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/rooms')
      .then(r => setRoomOptions(r.data))
      .catch(err => setError(err.response?.data?.message || 'Could not load available rooms. Please refresh and try again.'));
    if (isEdit) {
      api.get(`/students/${id}`).then(r => {
        const s = r.data;
        setForm({
          name: s.name || '', fatherName: s.fatherName || '', cnicNo: s.cnicNo || '',
          address: s.address || '', institute: s.institute || '', department: s.department || '',
          contactNo: s.contactNo || '', emergencyParentContact: s.emergencyParentContact || '',
          room: s.room || '', admissionFee: s.admissionFee || '', securityFee: s.securityFee || '',
          monthlyFee: s.monthlyFee || '', admissionFeePaid: s.admissionFeePaid || 'Unpaid',
          securityFeePaid: s.securityFeePaid || 'Unpaid',
          admissionDate: s.admissionDate ? s.admissionDate.split('T')[0] : new Date().toISOString().split('T')[0],
          status: s.status || 'Active'
        });
        if (s.photo) setPhotoPreview(mediaUrl(s.photo));
        setAccountInfo(s.account || null);
      }).catch(err => setError(err.response?.data?.message || 'Could not load the student record.'));
    }
  }, [id, isEdit]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Student photo must be an image file.');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setError('Student photo is too large. Please choose an image under 5 MB.');
      e.target.value = '';
      return;
    }
    setError('');
    if (photoPreview?.startsWith('blob:')) URL.revokeObjectURL(photoPreview);
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSignatureChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Student signature must be an image file.');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setError('Student signature is too large. Please choose an image under 5 MB.');
      e.target.value = '';
      return;
    }
    setError('');
    setSignature(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    setLoading(true);

    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append('createAccount', String(!accountInfo && accountForm.create));
      fd.append('accountUsername', accountForm.username);
      fd.append('accountPassword', accountForm.password);
      if (photo) fd.append('photo', photo);
      if (signature) fd.append('studentSignature', signature);

      if (isEdit) {
        await api.put(`/students/${id}`, fd);
        if (!accountInfo && accountForm.create) {
          const accountResponse = await api.post('/user-accounts', { role: 'student', personId: id, username: accountForm.username, password: accountForm.password });
          setCredentials({ name: form.name, username: accountResponse.data.account.username, password: accountResponse.data.temporaryPassword });
          setAccountInfo(accountResponse.data.account);
        }
        setSuccess('Student updated successfully!');
      } else {
        const response = await api.post('/students', fd);
        if (response.data.account) setCredentials({ name: response.data.name, username: response.data.account.username, password: response.data.temporaryPassword });
        setSuccess(response.data.account ? 'Student and portal login registered successfully!' : 'Student added successfully!');
        if (!response.data.account) setTimeout(() => navigate('/admin/students'), 1200);
        setForm(emptyForm);
        setPhoto(null);
        setPhotoPreview(null);
        setSignature(null);
      }
      if (isEdit && (accountInfo || !accountForm.create)) setTimeout(() => navigate('/admin/students'), 1200);
    } catch (err) {
      const apiMessage = typeof err.response?.data === 'string' ? '' : err.response?.data?.message;
      setError(apiMessage || (err.code === 'ECONNABORTED'
        ? 'The request took too long. Please check the server and try again.'
        : 'Student could not be saved. Please check the form and selected images.'));
    } finally {
      setLoading(false);
    }
  };

  const grouped = roomOptions.reduce((result, room) => {
    const floor = room.floor || 'Other';
    if (!result[floor]) result[floor] = [];
    result[floor].push(room);
    return result;
  }, {});

  return (
    <Layout title={isEdit ? 'Edit Student' : 'Add New Student'}>
      {credentials && <CredentialSuccessModal
        credential={credentials}
        title={isEdit ? 'Student login created!' : 'Student added successfully!'}
        onClose={() => setCredentials(null)}
        onContinue={() => navigate('/admin/students')}
      />}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">📋 Student Admission Form — Royal Tower Hostel</h3>
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/admin/students')}>← Back</button>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">✅ {success}</div>}
        <form onSubmit={handleSubmit} autoComplete="off">
          {/* Photo Upload */}
          <div className="student-photo-section" style={{ display: 'flex', gap: 20, alignItems: 'flex-start', marginBottom: 20, padding: '16px 0', borderBottom: '1px solid #e2e8f0' }}>
            <div>
              {photoPreview ? (
                <img src={photoPreview} alt="Preview" className="photo-upload-preview" />
              ) : (
                <div className="photo-placeholder">📷</div>
              )}
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#4a5568', display: 'block', marginBottom: 6 }}>
                STUDENT PHOTO
              </label>
              <div className="image-source-actions">
                <label className="btn btn-primary btn-sm">Upload image<input type="file" accept="image/jpeg,image/png,image/webp,image/avif" onChange={handlePhotoChange} /></label>
                <label className="btn btn-outline btn-sm">📷 Take photo<input type="file" accept="image/*" capture="user" onChange={handlePhotoChange} /></label>
              </div>
              <p style={{ fontSize: 11, color: '#718096', marginTop: 4 }}>JPG, PNG, WebP or AVIF, maximum 5 MB</p>
            </div>
          </div>

          {/* Personal Info */}
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#4a5568', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              👤 Personal Information
            </h4>
            <div className="form-grid">
              <div className="form-group">
                <label>Name *</label>
                <input value={form.name} maxLength={100} onChange={e => set('name', e.target.value)} required placeholder="Full name" />
              </div>
              <div className="form-group">
                <label>Father Name *</label>
                <input value={form.fatherName} maxLength={100} onChange={e => set('fatherName', e.target.value)} required placeholder="Father's full name" />
              </div>
              <div className="form-group">
                <label>CNIC No *</label>
                <input value={form.cnicNo} onChange={e => set('cnicNo', cnicInput(e.target.value))} required inputMode="numeric" pattern="[0-9]{5}-[0-9]{7}-[0-9]" maxLength={15} placeholder="17301-1234567-8" title="Enter exactly 13 CNIC digits" />
                <small>Exactly 13 digits</small>
              </div>
              <div className="form-group">
                <label>Contact No *</label>
                <input value={form.contactNo} onChange={e => set('contactNo', digitsOnly(e.target.value, 11))} required inputMode="numeric" pattern="03[0-9]{9}" maxLength={11} placeholder="03XXXXXXXXX" title="Enter 11 digits starting with 03" />
                <small>11 digits, starting with 03</small>
              </div>
              <div className="form-group">
                <label>Emergency Parent Contact *</label>
                <input value={form.emergencyParentContact} onChange={e => set('emergencyParentContact', digitsOnly(e.target.value, 11))} required inputMode="numeric" pattern="03[0-9]{9}" maxLength={11} placeholder="03XXXXXXXXX" title="Enter 11 digits starting with 03" />
                <small>11 digits, starting with 03</small>
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Address *</label>
                <textarea value={form.address} maxLength={500} onChange={e => set('address', e.target.value)} required placeholder="Complete home address" rows={2} />
              </div>
            </div>
          </div>

          {/* Academic Info */}
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#4a5568', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              🎓 Academic Information
            </h4>
            <div className="form-grid">
              <div className="form-group">
                <label>Institute *</label>
                <input value={form.institute} maxLength={150} onChange={e => set('institute', e.target.value)} required placeholder="University/College name" />
              </div>
              <div className="form-group">
                <label>Department *</label>
                <input value={form.department} maxLength={100} onChange={e => set('department', e.target.value)} required placeholder="e.g. Computer Science" />
              </div>
            </div>
          </div>

          {/* Room Assignment */}
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#4a5568', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              🏠 Room Assignment
            </h4>
            <div className="form-grid">
              <div className="form-group">
                <label>Assign Room *</label>
                <select value={form.room} onChange={e => set('room', e.target.value)} required>
                  <option value="">-- Select Room --</option>
                  {Object.entries(grouped).map(([floor, rooms]) => (
                    <optgroup key={floor} label={floor}>
                      {rooms.map(room => (
                        <option key={room.room} value={room.room} disabled={room.managementStatus !== 'Active' || (room.availableSlots < 1 && form.room !== room.room)}>
                          {room.room} — {room.count}/{room.capacity} beds {room.managementStatus !== 'Active' ? '(Inactive)' : room.availableSlots < 1 && form.room !== room.room ? '(Full)' : ''}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Admission Date</label>
                <input type="date" value={form.admissionDate} onChange={e => set('admissionDate', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select value={form.status} onChange={e => set('status', e.target.value)}>
                  <option>Active</option>
                  <option>Inactive</option>
                </select>
              </div>
            </div>
          </div>

          {/* Fee Structure */}
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#4a5568', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              💰 Fee Structure
            </h4>
            <div className="form-grid">
              <div className="form-group">
                <label>Admission Fee (PKR)</label>
                <input type="number" value={form.admissionFee} onChange={e => set('admissionFee', e.target.value)} placeholder="0" min="0" />
              </div>
              <div className="form-group">
                <label>Admission Fee Status</label>
                <select value={form.admissionFeePaid} onChange={e => set('admissionFeePaid', e.target.value)}>
                  <option>Unpaid</option>
                  <option>Paid</option>
                </select>
              </div>
              <div className="form-group">
                <label>Security Fee (PKR)</label>
                <input type="number" value={form.securityFee} onChange={e => set('securityFee', e.target.value)} placeholder="0" min="0" />
              </div>
              <div className="form-group">
                <label>Security Fee Status</label>
                <select value={form.securityFeePaid} onChange={e => set('securityFeePaid', e.target.value)}>
                  <option>Unpaid</option>
                  <option>Paid</option>
                </select>
              </div>
              <div className="form-group">
                <label>Monthly Fee (PKR) *</label>
                <input type="number" value={form.monthlyFee} onChange={e => set('monthlyFee', e.target.value)} required placeholder="0" min="0" />
              </div>
            </div>
          </div>

          {/* Portal Login */}
          <div style={{ marginBottom: 16, padding: 16, border: '1px solid #dbe9e4', borderRadius: 10, background: '#f5faf8' }}>
            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#315d53', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Portal Login Account</h4>
            {accountInfo ? <div><p style={{ fontSize: 13 }}><strong>@{accountInfo.username}</strong> <span className={`badge ${accountInfo.status === 'Active' ? 'badge-success' : 'badge-secondary'}`}>{accountInfo.status}</span></p><p style={{ fontSize: 11, color: '#718096', marginTop: 6 }}>Password and status are managed from User Accounts.</p></div> : <><label className="customizer-toggle" style={{ marginBottom: 14 }}><input type="checkbox" checked={accountForm.create} onChange={e => setAccountForm({ ...accountForm, create: e.target.checked })} /><span className="customizer-toggle-track"><span /></span><strong>Create student login now</strong></label>{accountForm.create && <div className="form-grid"><div className="form-group"><label>Username (optional)</label><input name="new-student-portal-username" autoComplete="off" value={accountForm.username} maxLength={40} pattern="[A-Za-z0-9][A-Za-z0-9._-]{3,39}" title="Use 4-40 letters, numbers, dots, dashes or underscores" onChange={e => setAccountForm({ ...accountForm, username: e.target.value })} /><small>Leave blank to create a username automatically.</small></div><div className="form-group"><label>Temporary Password (optional)</label><PasswordInput name="new-student-portal-password" autoComplete="new-password" value={accountForm.password} minLength={12} maxLength={100} onChange={e => setAccountForm({ ...accountForm, password: e.target.value })} /><p style={{ fontSize: 10, color: '#718096' }}>Leave blank for an easy-to-read 12-character password. Custom passwords require uppercase, lowercase, number and symbol.</p></div></div>}</>}
          </div>

          {/* Signature */}
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#4a5568', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              ✍️ Signature
            </h4>
            <div className="form-grid">
              <div className="form-group">
                <label>Student Signature (optional)</label>
                <input type="file" accept="image/*" onChange={handleSignatureChange} />
                <p style={{ fontSize: 11, color: '#718096' }}>Image file, maximum 5 MB</p>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-outline" onClick={() => navigate('/admin/students')}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : isEdit ? '💾 Update Student' : '✅ Add Student'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
